import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Skeleton } from '@/components/ui/Skeleton';
import { useSubmitAttestation } from '@/hooks/api/useAttestations';
import { useChiefComplaints } from '@/hooks/api/useDashboard';
import { GOALS_COMMITMENT_TEXT } from '@/pages/login/attestationContent';
import { usePeriod } from '@/state/PeriodContext';

import { toGoalRows, toGoalSnapshots, type GoalRow } from './goalRows';
import styles from './GoalsCommitmentModal.module.css';

/**
 * Goals commitment (PLA-1110). Opened from the reminder banner; never blocks
 * the page. Lists the organization-set target for each chief complaint next
 * to the user's score in the active period, then records one commitment
 * covering that snapshot.
 */
interface GoalsCommitmentModalProps {
  open: boolean;
  onClose: () => void;
  /** Called once the API has recorded the commitment. */
  onCommitted: () => void;
}

const STATUS_LABEL: Record<GoalRow['status'], string> = {
  met: 'At goal',
  below: 'Below goal',
  no_data: 'No data yet',
};

export function GoalsCommitmentModal({ open, onClose, onCommitted }: GoalsCommitmentModalProps) {
  const { periodMeta } = usePeriod();
  const complaints = useChiefComplaints({ dateRange: periodMeta.api });
  const submit = useSubmitAttestation();
  const [done, setDone] = useState(false);

  const rows = useMemo(() => toGoalRows(complaints.data ?? []), [complaints.data]);
  const metCount = rows.filter((r) => r.status === 'met').length;
  const scoredCount = rows.filter((r) => r.status !== 'no_data').length;

  const handleCommit = () => {
    if (submit.isPending || rows.length === 0) return;
    submit.mutate(
      { kind: 'goals', text: GOALS_COMMITMENT_TEXT, goals: toGoalSnapshots(rows) },
      {
        onSuccess: () => {
          setDone(true);
          onCommitted();
        },
      },
    );
  };

  const handleClose = () => {
    if (submit.isPending) return;
    onClose();
  };

  const canCommit = complaints.isSuccess && rows.length > 0 && !done;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      labelledBy="goals-commitment-title"
      fullScreenOnMobile
      title={
        <h2 id="goals-commitment-title" className={styles.title}>
          {done ? 'Commitment recorded' : 'Your goals'}
        </h2>
      }
      subtitle={
        done
          ? 'Thank you. You can review these goals any time from Home and Lens.'
          : `Organization targets for each chief complaint, next to your score for ${periodMeta.label.toLowerCase()}.`
      }
      footer={
        done ? (
          <div className={styles.actions}>
            <Button size="lg" onClick={onClose} className={styles.primary}>
              Done
            </Button>
          </div>
        ) : (
          <div className={styles.actions}>
            <Button
              variant="ghost"
              onClick={handleClose}
              disabled={submit.isPending}
              className={styles.secondary}
            >
              Not now
            </Button>
            <Button
              size="lg"
              onClick={handleCommit}
              disabled={!canCommit}
              loading={submit.isPending}
              className={styles.primary}
            >
              I commit to these goals
            </Button>
          </div>
        )
      }
    >
      {done ? (
        <div className={styles.done} role="status">
          <span className={styles.doneMark} aria-hidden="true">
            ✓
          </span>
          <p>
            Your commitment to {rows.length} {rows.length === 1 ? 'goal' : 'goals'} has been
            recorded.
          </p>
        </div>
      ) : complaints.isPending ? (
        <ul className={styles.list} aria-busy="true">
          {[0, 1, 2, 3].map((i) => (
            <li key={i} className={styles.row}>
              <Skeleton variant="block" height={44} label={i === 0 ? 'Loading goals' : undefined} />
            </li>
          ))}
        </ul>
      ) : complaints.isError ? (
        <div className={styles.state} role="alert">
          <strong>We couldn&rsquo;t load your goals.</strong>
          <p>Check your connection and try again.</p>
          <Button variant="secondary" size="sm" onClick={() => void complaints.refetch()}>
            Try again
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.state} role="status">
          <strong>No goals have been set yet.</strong>
          <p>Once your organization sets targets in Vida they will appear here.</p>
        </div>
      ) : (
        <>
          <p className={styles.summary}>
            <strong>
              {metCount} of {scoredCount}
            </strong>{' '}
            {scoredCount === 1 ? 'goal' : 'goals'} at target
            {rows.length > scoredCount && (
              <span className={styles.summaryMuted}>
                {' '}
                · {rows.length - scoredCount} without data
              </span>
            )}
          </p>

          <ul className={styles.list}>
            {rows.map((row) => (
              <li key={row.id} className={styles.row} data-status={row.status}>
                <div className={styles.rowHead}>
                  <span className={styles.name}>{row.name}</span>
                  <span className={styles.status}>{STATUS_LABEL[row.status]}</span>
                </div>
                <div
                  className={styles.bar}
                  role="img"
                  aria-label={
                    row.score == null
                      ? `${row.name}: no score yet, goal ${row.goal}%`
                      : `${row.name}: ${row.score}% of a ${row.goal}% goal`
                  }
                >
                  {row.score != null && (
                    <span className={styles.fill} style={{ width: `${row.score}%` }} />
                  )}
                  <span className={styles.tick} style={{ left: `${row.goal}%` }} />
                </div>
                <div className={styles.figures}>
                  <span>
                    {row.score == null ? '--' : `${row.score}%`}
                    <span className={styles.figureLabel}> now</span>
                  </span>
                  <span>
                    {row.goal}%<span className={styles.figureLabel}> goal</span>
                    {row.isDefault && <span className={styles.figureLabel}> (default)</span>}
                  </span>
                </div>
              </li>
            ))}
          </ul>

          <p className={styles.statement}>{GOALS_COMMITMENT_TEXT}</p>

          {submit.isError && (
            <div className={styles.alert} role="alert">
              We couldn&rsquo;t record your commitment. Please try again.
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
